import {
    createApi,
    fakeBaseQuery,
} from '@reduxjs/toolkit/query';
import {
    AbstractedHardwareDetailGroupResponse,
    AbstractedTariffWithHardwareResponse,
    GladosEndpointVersion,
    GladosServiceFactory,
    HardwareDetailGroupParamsV1,
    TariffWithHardwareParamsV1,
} from '@vfde-sails/glados-v2';
import { getQueryParam } from '@vfde-sails/utils';
import { RootState } from '../app/store';
import {
    BTX_BNT,
    FinancingType,
    MAP_BTX_TO_API_BTX,
    MAP_SALESCHANNEL_TO_API_SALESCHANNEL,
    RedTariff,
    YoungTariff,
} from '@vfde-sails/constants';
import { selectCustomerConfig } from '../features/app/slice';
import {
    getAllDiscounts,
    getSubscriptionIds,
} from '@vfde-sails/page-options';
import { CustomerConfig } from '../interfaces/state';
import { isAxiosError } from 'axios';
import { RequestError } from '@vfde-sails/types';

/**
 * Argument object for `getHardwareDetailGroup` endpoint
 */
export interface GetHardwareDetailGroupArgs {
    /**
     * Subscription ID
     */
    subscriptionId?: CustomerConfig['subscriptionId'];
}

/**
 * Argument object for `getTariffWithHardware` endpoint
 */
export interface GetTariffWithHardwareArgs {
    /**
     * The atomic IDs to send.
     * Normally they wouldn't need to be part of the args,
     * but using the `selectAtomicIds` selector inside the queryFn
     * procudes follow error due to a circular dependency:
     * `Uncaught ReferenceError: Cannot access 'selectCustomerConfig' before initialization`.
     */
    atomicIds: string[];
    /**
     * Boolean tauschbonus flag
     */
    isTauschbonus: boolean;
}

const createRequestError = (error: unknown): RequestError => {
    if (isAxiosError(error)) {
        return {
            code: error.code,
            status: error.response?.status,
            message: error.message,
        };
    }

    return {
        message: error instanceof Error ? error.message : `${error}`,
    };
};

export const gladosApi = createApi({
    reducerPath: 'Glados',
    baseQuery: fakeBaseQuery<RequestError>(),
    endpoints: build => ({
        getHardwareDetailGroup: build.query<AbstractedHardwareDetailGroupResponse, GetHardwareDetailGroupArgs>({
            queryFn: async (args, api) => {
                const state = api.getState() as RootState;
                const customerConfig = selectCustomerConfig(state);

                const {
                    deviceId,
                    salesChannel,
                    isGigakombi,
                    isTradeIn,
                    isTauschbonus,
                    isRestlaufzeit,
                } = customerConfig;

                const { subscriptionId } = args;
                const subscriptionIds = subscriptionId ? [subscriptionId] : [];

                const discountIds = getAllDiscounts({
                    salesChannel,
                    deviceId,
                    subscriptionIds,
                    isGigakombi,
                    isTradeIn,
                    isTauschbonus,
                    isRestlaufzeit,
                });

                const params: HardwareDetailGroupParamsV1 = {
                    btx: MAP_BTX_TO_API_BTX[BTX_BNT],
                    salesChannel: [MAP_SALESCHANNEL_TO_API_SALESCHANNEL[salesChannel]],
                    discountIds,
                    tariffIds: subscriptionIds,
                    groupId: deviceId!,
                };

                const hardwareService = GladosServiceFactory.getHardwareService();



                try {
                    const response = await hardwareService.getHardwareDetailGroup(
                        GladosEndpointVersion.V1,
                        params,
                    );



                    return { data: response.data };
                }
                catch (error) {
                    return {
                        error: createRequestError(error),
                    };
                }
            },
        }),
        getTariffWithHardware: build.query<AbstractedTariffWithHardwareResponse<RedTariff | YoungTariff>, GetTariffWithHardwareArgs>({
            queryFn: async (args: GetTariffWithHardwareArgs, api) => {
                const state = api.getState() as RootState;
                const customerConfig = selectCustomerConfig(state);

                const {
                    deviceId,
                    salesChannel,
                    isGigakombi,
                    isTradeIn,
                    isRestlaufzeit,
                } = customerConfig;

                const { isTauschbonus } = args;
                const subscriptionIds = getSubscriptionIds(salesChannel, FinancingType.SubLevel);

                const discountIds = getAllDiscounts({
                    salesChannel,
                    deviceId,
                    subscriptionIds,
                    isGigakombi,
                    isTradeIn,
                    isTauschbonus,
                    isRestlaufzeit,
                });

                const params: TariffWithHardwareParamsV1 = {
                    btx: MAP_BTX_TO_API_BTX[BTX_BNT],
                    salesChannel: [MAP_SALESCHANNEL_TO_API_SALESCHANNEL[customerConfig.salesChannel]],
                    atomicIds: args.atomicIds,
                    tariffIds: subscriptionIds,
                    discountIds,
                };

                const tariffService = GladosServiceFactory.getTariffService();



                try {
                    const response = await tariffService.getTariffWithHardware<GladosEndpointVersion, RedTariff | YoungTariff>(
                        GladosEndpointVersion.V1,
                        params,
                    );



                    return { data: response.data };
                }
                catch (error) {
                    return {
                        error: createRequestError(error),
                    };
                }
            },
        }),
    }),
});

export const {
    getHardwareDetailGroup,
    getTariffWithHardware,
} = gladosApi.endpoints;
